/**
 * The DOM/canvas edge of the pre-call photo quality check.
 *
 * Decodes a File, draws it into a small measurement canvas, and hands the
 * pixels to the DOM-free math in lib/analysis/photo-quality.ts.
 *
 * Contract: this NEVER throws and NEVER rejects. Every failure mode (SSR, no
 * 2d context, decode failure, tainted canvas, hung decode, zero dimensions)
 * resolves `null`, which callers must read as "no warning". The check is
 * advisory and must never be able to break an upload.
 */

import { decodeImage, type DecodedImage } from '@/lib/image-compression'
import { evaluateImageQuality, type PhotoQualityVerdict } from './photo-quality'

/**
 * Longest-edge target for the measurement copy. ~49k pixels at 4:3, which is
 * two linear passes and sub-millisecond. Small enough that the second decode
 * never shows up on the happy path.
 */
export const SAMPLE_MAX_EDGE = 256

/**
 * A hung decode must not leave the warning state pending forever. The measure
 * runs alongside the upload fetch, so this ceiling only bounds the warning.
 */
export const MEASURE_TIMEOUT_MS = 1200

export interface MeasureOptions {
  /** Longest-edge target for the measurement copy. Default SAMPLE_MAX_EDGE. */
  maxEdge?: number
  /** Default MEASURE_TIMEOUT_MS. */
  timeoutMs?: number
}

/** Target box preserving aspect ratio. Never upscales; never returns 0. */
function targetBox(
  width: number,
  height: number,
  maxEdge: number,
): { width: number; height: number } {
  const longest = Math.max(width, height)
  const scale = longest > 0 ? Math.min(1, maxEdge / longest) : 1
  return {
    width: Math.max(1, Math.floor(width * scale)),
    height: Math.max(1, Math.floor(height * scale)),
  }
}

async function measure(
  file: File,
  maxEdge: number,
  signal?: AbortSignal,
): Promise<PhotoQualityVerdict | null> {
  let decoded: DecodedImage | null = null
  try {
    decoded = await decodeImage(file, { signal })
    if (!decoded || !decoded.width || !decoded.height) return null

    const box = targetBox(decoded.width, decoded.height, maxEdge)

    const canvas = document.createElement('canvas')
    canvas.width = box.width
    canvas.height = box.height

    const ctx = canvas.getContext('2d')
    if (!ctx) return null

    ctx.drawImage(decoded.source, 0, 0, box.width, box.height)

    let pixels: ImageData
    try {
      // Throws SecurityError on a tainted canvas (cross-origin source).
      pixels = ctx.getImageData(0, 0, box.width, box.height)
    } catch {
      return null
    }
    if (!pixels || !pixels.data) return null

    return evaluateImageQuality({
      data: pixels.data,
      width: pixels.width,
      height: pixels.height,
    })
  } catch {
    return null
  } finally {
    try {
      decoded?.cleanup()
    } catch {
      // A failed cleanup must not surface either.
    }
  }
}

/**
 * Returns null on ANY failure: SSR, no canvas 2d context, decode failure,
 * tainted canvas, timeout, zero dimensions. Never throws, never rejects.
 */
export async function measurePhotoQuality(
  file: File,
  options?: MeasureOptions,
): Promise<PhotoQualityVerdict | null> {
  try {
    if (typeof document === 'undefined') return null
    if (!file) return null

    const maxEdge = options?.maxEdge ?? SAMPLE_MAX_EDGE
    const timeoutMs = options?.timeoutMs ?? MEASURE_TIMEOUT_MS

    // The abort exists for resource hygiene, not cancellation semantics: a
    // decode that hangs past the timeout would otherwise strand its object URL
    // forever, because measure()'s cleanup only runs once the decode settles.
    const controller =
      typeof AbortController !== 'undefined' ? new AbortController() : undefined
    let timer: ReturnType<typeof setTimeout> | undefined
    const timeout = new Promise<null>((resolve) => {
      timer = setTimeout(() => {
        try {
          controller?.abort()
        } catch {
          // Fail open: the timeout must still resolve null.
        }
        resolve(null)
      }, timeoutMs)
    })

    try {
      return await Promise.race([measure(file, maxEdge, controller?.signal), timeout])
    } finally {
      if (timer !== undefined) clearTimeout(timer)
    }
  } catch {
    return null
  }
}
